import React, { Component } from 'react';
import ApiUrls from '@api/apiUrls';
import { fetchApi } from '@api/fetchApi';
import {
  PasswordStrengthMeter,
  PasswordStrengthMeterContext,
} from '@components-member/PasswordStrengthMeter/PasswordStrengthMeter';
import { IFields, IFormConfig, IFormContainerProps } from '@components-member/TradingAccounts/interfaces';
import { MessageDataType, UserLeverageHint } from '@components-member/shared/UserLeverageHint';
import { FormType } from '@components-shared/forms/formField.interface';
import { NotificationDialog } from '@components-shared/modal';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import generator, { getInitialState } from 'components/shared/forms/formGenerator';

interface IState {
  form: FormType & { leverage?: number; password: string };
  tradingSettings: MessageDataType;
}

const FormContainer = (formConfig: IFormConfig) => {
  const formState = getInitialState(formConfig.fields);
  const Form = generator(formConfig.fields);
  const isFormReset = formConfig?.resetFields;

  return class FormComponent extends Component<IFormContainerProps, IState> {
    form: React.RefObject<{ update: (data: IFields[]) => void }>;

    constructor(props: IFormContainerProps) {
      super(props);

      this.state = {
        form: this.getDefaultFormState(),
        tradingSettings: {
          leverageWarnings: {},
        },
      };

      this.form = React.createRef();
    }

    getDefaultFormState = () => {
      const { account } = this.props;

      return account ? { leverage: account?.leverage } : { ...formState };
    };

    async componentDidMount() {
      const { fields } = formConfig;
      const { account } = this.props;
      const idx = fields.findIndex((f) => f.name === 'leverage');

      if (idx > -1 && account) {
        const { leverages } = account;

        const options = leverages.map((o: number) => ({ value: o }));

        fields[idx] = {
          ...fields[idx],
          options,
        };

        this.form.current?.update(fields);
      }

      await this.getTradingSettings();
    }

    getTradingSettings = async () => {
      try {
        const response = await fetchApi.get<MessageDataType>(ApiUrls.userTradingAccountSettings.get);

        this.setState({ tradingSettings: response.data });
      } catch (error) {
        handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
      }
    };

    resetFormState = () => {
      if (isFormReset) {
        this.setState({ form: { ...formState } });
      }
      return;
    };

    getAction = () => {
      const { api, accountId } = this.props;
      const { form } = this.state;

      form.tradingAccountId = accountId;

      return fetchApi.post(api, form);
    };

    onAction = async () => {
      const { errorMessage, successMessage } = formConfig;

      try {
        await this.getAction();
        this.resetFormState();

        return NotificationDialog.show(successMessage);
      } catch (error) {
        handleError(errorMessage, error);
      }
    };

    onChange = (name: string, value: string) => {
      const { form } = this.state;

      form[name] = value;

      this.setState({ form });
    };

    beforeActionContent = () => {
      const isLeverageForm = formConfig.fields.some((field) => field.name === 'leverage');

      if (!isLeverageForm) {
        return;
      }

      const { leverageWarnings } = this.state.tradingSettings;

      if (!Object.keys(leverageWarnings).length) {
        return;
      }

      return <UserLeverageHint message={this.state.tradingSettings.leverageWarnings} />;
    };

    render() {
      const { title, formClass } = this.props;
      const actions = [
        {
          title,
          onClick: this.onAction,
        },
      ];
      const { form } = this.state;

      return (
        <div className="form-box form-box--columns">
          <PasswordStrengthMeterContext.Provider value={<PasswordStrengthMeter newPassword={form.password} />}>
            <Form
              ref={this.form}
              className={`admin--form ${formClass || 'column--form'}`}
              form={form}
              onChange={this.onChange}
              actions={actions}
              beforeActions={this.beforeActionContent()}
            />
          </PasswordStrengthMeterContext.Provider>
        </div>
      );
    }
  };
};

export default FormContainer;
